import React from 'react';
import ButtonLoader from './iconSvgs/buttonloader';

interface ButtonProps {
  variant?: 'primary' | 'dark' | 'secondary' | 'light' | 'negative';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean; // Added loading prop
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'lg',
  className = '',
  children,
  onClick,
  type = 'button',
  disabled = false,
  loading = false, // Default to false
}) => {
  // Base styles for all buttons
  const baseStyles = "inline-flex items-center justify-center rounded-md"; // Slightly rounded rectangle
  
  // Size variations
  const sizeStyles = {
    sm: "px-[15px] py-[8px] min-h-[38px] text-[14px]",
    md: "px-[20px] py-[10px] min-h-[48px] text-[16px]",
    lg: "px-[20px] py-[15px] min-h-[58px]",
  };
  
  // Color variants
  const variantStyles = {
    primary: "bg-[#D22630] border-[#D22630] text-white hover:bg-opacity-90",
    dark: "bg-[#4b0132] border-[#200418] text-white hover:bg-opacity-90",
    secondary: "bg-[#3e042d] border-[#3e042d] text-white hover:bg-opacity-90",
    light: "bg-[#740c4a] border-[#740c4a] text-white hover:bg-opacity-90",
    negative: "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
  };
  
  // Disabled state
  const disabledStyles = (disabled || loading) ? "opacity-50 cursor-not-allowed" : "cursor-pointer";

  // Button style with relative positioning for loader
  const buttonStyle: React.CSSProperties = { position: 'relative' };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} ${disabledStyles} ${className}`}
      style={buttonStyle}
      aria-busy={loading}
    >
      {loading ? (
        <span style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
          <ButtonLoader
            size={20}
            className="w-5 h-5"
          />
        </span>
      ) : (
        children
      )}
    </button>
  );
};

export default Button;